import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useColors } from '../../../../../theme/ColorModeContext';
import { StageSection, Prose, FactGrid, NumberedFlow, Callout, MiniLabel, Panel, SegmentedToggle } from '../../ContentPrimitives';
import { StageBlock, content, question } from '../../types';
import { CantonParty } from './cantonModel';
import { PartyChip } from './cantonVisuals';

export const meta = {
    title: 'Participant Nodes',
    tagline: 'Where an organization\'s own slice of the ledger actually lives.',
};

interface NodeOption {
    id: string;
    label: string;
    hosts: CantonParty[] | 'everything';
}

const NODES: NodeOption[] = [
    { id: 'custodian', label: 'Custodian node', hosts: ['Alice', 'Bob'] },
    { id: 'bank', label: 'Bank node', hosts: ['Bank'] },
    { id: 'registry', label: 'Registry node', hosts: ['Registry'] },
    { id: 'carol', label: 'Carol\'s node', hosts: ['Carol'] },
    { id: 'full', label: 'Ethereum full node', hosts: 'everything' },
];

const NETWORK_CONTRACTS: { id: number; label: string; stakeholders: CantonParty[] }[] = [
    { id: 1, label: 'Iou $100 · Bank owes Bob', stakeholders: ['Bank', 'Bob'] },
    { id: 2, label: 'Share · 10 ACME held by Alice', stakeholders: ['Registry', 'Alice'] },
    { id: 3, label: 'Iou $250 · Bank owes Carol', stakeholders: ['Bank', 'Carol'] },
    { id: 4, label: 'Loan $1,000 · Bank ⇄ Carol', stakeholders: ['Bank', 'Carol'] },
    { id: 5, label: 'Share · 40 ACME held by Carol', stakeholders: ['Registry', 'Carol'] },
    { id: 6, label: 'TradeConfirmation · Alice ⇄ Bob', stakeholders: ['Alice', 'Bob', 'Registry'] },
    { id: 7, label: 'Iou $75 · Bank owes Alice', stakeholders: ['Bank', 'Alice'] },
    { id: 8, label: 'Share · 5 ACME held by Bob', stakeholders: ['Registry', 'Bob'] },
];

const NodeStoreExplorer = () => {
    const colors = useColors();
    const [nodeId, setNodeId] = useState('bank');
    const node = NODES.find((item) => item.id === nodeId) ?? NODES[0];
    const stores = (stakeholders: CantonParty[]) =>
        node.hosts === 'everything' || stakeholders.some((party) => (node.hosts as CantonParty[]).includes(party));
    const storedCount = NETWORK_CONTRACTS.filter((item) => stores(item.stakeholders)).length;
    const share = storedCount / NETWORK_CONTRACTS.length;

    return (
        <Panel>
            <MiniLabel>Pick a node</MiniLabel>
            <SegmentedToggle<string> ariaLabel="Node" value={nodeId} onChange={setNodeId} options={NODES.map((item) => ({ value: item.id, label: item.label }))} />

            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', mt: 2 }}>
                <Typography sx={{ color: colors.text.muted, fontSize: '0.8rem' }}>hosts</Typography>
                {node.hosts === 'everything'
                    ? <Typography sx={{ color: colors.text.secondary, fontSize: '0.88rem' }}>no one in particular: it replicates the entire chain</Typography>
                    : node.hosts.map((party) => <PartyChip key={party} party={party} />)}
            </Box>

            <Box sx={{ mt: 2, mb: 2 }} aria-live="polite">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography sx={{ color: colors.text.primary, fontWeight: 800, fontSize: '0.92rem' }}>
                        Stores {storedCount} of {NETWORK_CONTRACTS.length} contracts on the network
                    </Typography>
                    <Typography sx={{ color: colors.text.muted, fontSize: '0.85rem', fontFamily: '"Share Tech Mono", monospace' }}>{Math.round(share * 100)}%</Typography>
                </Box>
                <Box sx={{ height: 8, borderRadius: '4px', backgroundColor: colors.bg.surface, overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${share * 100}%`, background: colors.gradient.primary, transition: 'width 0.35s ease' }} />
                </Box>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: '10px' }}>
                {NETWORK_CONTRACTS.map((contract) => {
                    const stored = stores(contract.stakeholders);
                    return (
                        <Box key={contract.id} sx={{
                            p: '10px 12px',
                            borderRadius: '8px',
                            border: `1px solid ${stored ? colors.border.accent : colors.border.subtle}`,
                            backgroundColor: stored ? colors.bg.primary : 'transparent',
                            opacity: stored ? 1 : 0.4,
                            transition: 'all 0.25s ease',
                        }}>
                            <Typography sx={{ color: colors.text.primary, fontSize: '0.86rem', fontWeight: 700 }}>
                                <Box component="span" sx={{ color: colors.text.muted, fontFamily: '"Share Tech Mono", monospace', mr: 1 }}>#{contract.id}</Box>
                                {contract.label}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap', mt: 0.75 }}>
                                {contract.stakeholders.map((party) => <PartyChip key={party} party={party} dimmed={!stored} />)}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Panel>
    );
};

// This stage expects 2 quiz questions configured in the admin panel, in this order:
//   Q0 - what a participant node actually stores
//   Q1 - how its storage footprint compares to a Bitcoin/Ethereum full node
export const blocks: StageBlock[] = [
    content('intro', () => (
        <StageSection gradientBorder eyebrow="Stage 4 of 8" title="Each organization runs its own node">
            <Prose>
                A <strong>participant node</strong> is the piece of Canton infrastructure an organization operates to
                take part in the network. Parties don't connect to Canton directly: each party is{' '}
                <strong>hosted</strong> on one or more participant nodes, and the node acts on its behalf. A bank
                might run one node hosting the bank itself plus thousands of its clients as individual parties. On
                the public Canton Network, these nodes are usually called <strong>validators</strong>.
            </Prose>
            <FactGrid items={[
                ['Stores', 'A private contract store: the active contracts (and their history) for the parties it hosts. Nothing else.'],
                ['Runs', 'The DAML engine, which interprets commands into transactions and re-executes incoming ones to validate them.'],
                ['Serves', 'The Ledger API (gRPC and JSON), which applications use to submit commands and read their parties\' contracts.'],
                ['Connects', 'To one or more synchronizers, through which it exchanges encrypted messages with other nodes.'],
            ]} />
            <Prose>
                Here's what happens when Alice's application wants to transfer an Iou:
            </Prose>
            <NumberedFlow steps={[
                ['Command', 'The app calls the Ledger API on Alice\'s node: "as Alice, exercise Transfer on contract #12 with newOwner = Bob".'],
                ['Interpretation', 'The node loads #12 from its own store, runs the DAML code, and produces a full transaction: archive #12, create a new Iou owned by Bob.'],
                ['Distribution', 'The node splits the transaction into views, encrypts each one for the nodes hosting its informees, and sends them off through a synchronizer.'],
                ['Commit', 'Once the transaction is approved, every involved node updates its own store: #12 is archived and the new contract is added, with the same result everywhere it matters.'],
            ]} />
            <Callout title="Hosting is flexible">
                A node can host many parties, and one party can be hosted on several nodes, for resilience or so a
                service provider and its client can both run infrastructure. Each hosting comes with a permission
                level: <strong>submission</strong> (may act for the party), <strong>confirmation</strong> (may approve
                transactions for it), or <strong>observation</strong> (read-only).
            </Callout>
        </StageSection>
    )),

    question(0),

    content('storage-footprint', () => (
        <StageSection title="Only what's relevant, nothing more">
            <Prose>
                A Bitcoin or Ethereum full node stores the network's entire history, because verifying everything
                requires having everything. A Canton participant node stores far less: only the contracts in which
                at least one of its hosted parties is a stakeholder. An organization with no involvement in a given
                agreement never receives, and never stores, that agreement's data. It's a direct consequence of the
                privacy model from the previous stage.
            </Prose>
            <Prose>
                Try each node below and compare it with the Ethereum-style full node at the end of the list.
            </Prose>
            <NodeStoreExplorer />
            <Prose>
                This has consequences that feel strange coming from public chains:
            </Prose>
            <FactGrid items={[
                ['No global explorer', 'No node holds the whole ledger, so there\'s no Etherscan-style view of all activity. Each organization sees only its own business.'],
                ['Storage scales with your business', 'A node\'s footprint grows with its own parties\' activity, not with the whole network\'s. Old archived data can be pruned.'],
                ['Your data, your responsibility', 'Nobody else keeps a full copy of your contracts. Losing a node without backups isn\'t fixed by re-syncing from peers the way a Bitcoin node would be.'],
                ['Your node, your verdicts', 'Your node validates everything that concerns you with its own copy of the DAML code. You don\'t have to trust your counterparty\'s node.'],
            ]} />
            <Callout tone="security" title="Security angle: the node is the trust boundary">
                Because the node holds your parties' data and acts on their behalf, compromising a participant node
                compromises every party it hosts: their confidential contracts, and their ability to submit and
                approve transactions. Access to the Ledger API, and which users may act as which parties, is where
                many real-world Canton deployments put their security effort.
            </Callout>
        </StageSection>
    )),

    question(1),
];
